import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLocale } from "../i18n/LocaleContext";
import { TREATMENTS, CATEGORIES } from "../data/treatments";
import { Reveal, Eyebrow } from "../components/Reveal";
import { scrollToElement } from "../lib/scroll";

export default function Category() {
  const { t, link, lang, category } = useLocale();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => scrollToElement(el), 600);
    }
  }, [location.hash, lang]);

  if (!category) return null;
  const items = TREATMENTS.filter((tr) => tr.category === category.key);

  return (
    <div data-testid={`category-page-${category.key}`}>
      <section className="mx-auto max-w-[1600px] px-6 pb-10 pt-40 sm:px-10 lg:px-16 lg:pt-52">
        <Reveal>
          <Link to={link("treatments")} data-testid="category-back-link" className="link-line text-[11px] font-label uppercase tracking-[0.25em] text-imperium-bronze">
            {t("nav.treatments")}
          </Link>
          <h1 className="mt-6 max-w-3xl font-serif text-5xl font-light leading-tight text-imperium-black sm:text-6xl">{category.names[lang]}</h1>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-imperium-black/70">{category.desc[lang]}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-16 sm:px-10 lg:px-16 lg:py-20">
        <div className="flex flex-col gap-24 lg:gap-32">
          {items.map((tr, i) => (
            <div key={tr.key} id={tr.slugs[lang]} data-testid={`category-treatment-${tr.key}`} className="scroll-mt-28">
              <Reveal>
                <div className="grid items-center gap-10 lg:grid-cols-12">
                  <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <div className="group overflow-hidden">
                      <img src={tr.img} alt={tr.names[lang]} loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                    </div>
                  </div>
                  <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1 lg:pr-10" : "lg:col-start-7"}`}>
                    <span className="font-serif italic text-2xl text-imperium-champagne">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="mt-4 font-serif text-3xl font-light text-imperium-black sm:text-4xl">{tr.names[lang]}</h2>
                    <div className="mt-5 h-px w-16 bg-imperium-gold" aria-hidden="true" />
                    <p className="mt-6 max-w-md font-serif text-xl font-light leading-relaxed text-imperium-black/85">{tr.short[lang]}</p>
                    <p className="mt-4 max-w-md text-base font-light leading-relaxed text-imperium-black/65">{tr.desc[lang]}</p>
                    <div className="mt-8 flex flex-wrap items-center gap-8">
                      {!tr.noPage && (
                        <Link
                          to={link("treatments", tr.slugs[lang])}
                          data-testid={`category-more-${tr.key}`}
                          className="group inline-flex items-center gap-2 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown"
                        >
                          <span className="link-line">{t("cta.readMore")}</span>
                          <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
                        </Link>
                      )}
                      <Link to={link("contact")} data-testid={`category-cta-${tr.key}`} className="link-line text-[11px] font-label uppercase tracking-[0.25em] text-imperium-bronze">
                        {t("cta.request")}
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 pb-24 sm:px-10 lg:px-16 lg:pb-32">
        <Reveal>
          <div className="mb-10 flex items-center gap-6">
            <h2 className="text-[11px] font-label uppercase tracking-[0.35em] text-imperium-bronze">{t("treatmentsPage.eyebrow")}</h2>
            <div className="h-px flex-1 bg-imperium-champagne/50" aria-hidden="true" />
          </div>
        </Reveal>
        {CATEGORIES.filter((c) => c.key !== category.key).map((c, i) => (
          <Reveal key={c.key} delay={0.05 * i}>
            <Link
              to={link("treatments", c.slugs[lang])}
              data-testid={`category-related-${c.key}`}
              className="group flex items-center justify-between border-t border-imperium-champagne/40 py-6 transition-all duration-500 last:border-b hover:pl-4"
            >
              <span className="font-serif text-2xl font-light text-imperium-black transition-colors group-hover:text-imperium-bronze sm:text-3xl">{c.names[lang]}</span>
              <ArrowUpRight className="h-5 w-5 text-imperium-bronze transition-transform duration-500 group-hover:rotate-45" />
            </Link>
          </Reveal>
        ))}
      </section>
    </div>
  );
}
